import { omit } from "lodash";
import { eachDayOfInterval } from "date-fns";
import { useState, useEffect } from "react";
import {
  NewDriverShiftAssignmentInput,
  GetDriverShiftAssignmentDetailsQuery,
  useAddDriverShiftAssignmentMutation,
  useEditDriverShiftAssignmentMutation,
  useGetDriverShiftAssignmentDetailsQuery,
  useAddDriverShiftMutation,
  NewDriverShiftInput,
  useGetDriverShiftDetailsQuery,
  useEditDriverShiftMutation,
  useGetDriverDetailsQuery,
} from "../../../graphql/generated";
import DriverShiftAssignmentFormModal, {
  DriverShiftAssignmentFormModalProps,
} from "./DriverShiftAssignmentFormModal";
import LoadingOverlay from "../../common/LoadingOverlay";
import { utcToZonedTime } from "date-fns-tz";

type DriverShiftAssignmentFormModalContainerProps = Omit<
  DriverShiftAssignmentFormModalProps,
  "driverShiftAssignment" | "onSubmit" | "error"
> & {
  driverShiftAssignmentId?: string;
  onSaved?: (driverShiftAssignmentId: string) => void;
};

type DriverShiftAssignmentDetailsData = Exclude<
  GetDriverShiftAssignmentDetailsQuery["driverShiftAssignment"],
  null | undefined
>;

const driverShiftAssignmentToInput = (
  driverShiftAssignment: DriverShiftAssignmentDetailsData | null
): NewDriverShiftAssignmentInput | null => {
  if (!driverShiftAssignment) {
    return null;
  }
  return {
    ...omit(driverShiftAssignment, "_id", "driver", "driverShift"),
  };
};

const DriverShiftAssignmentFormModalContainer = (
  props: DriverShiftAssignmentFormModalContainerProps
) => {
  const [submissionError, setSubmissionError] = useState<Error | null>(null);

  const driverShiftAssignmentQuery = useGetDriverShiftAssignmentDetailsQuery(
    {
      id: props.driverShiftAssignmentId || "",
    },
    {
      enabled: !!props.driverShiftAssignmentId,
    }
  );

  const addDriverShiftAssignmentMutation =
    useAddDriverShiftAssignmentMutation();
  const editDriverShiftAssignmentMutation =
    useEditDriverShiftAssignmentMutation();
  const addDriverShiftMutation = useAddDriverShiftMutation();
  const editDriverShiftMutation = useEditDriverShiftMutation();

  const existingDriverShiftAssignment =
    driverShiftAssignmentQuery?.data?.driverShiftAssignment || null;

  // Clear error when modal is opened/closed
  useEffect(() => {
    if (props.isOpen) {
      setSubmissionError(null);
    }
  }, [props.isOpen]);

  const submitDriverShiftAssignment = async (
    driverShiftAssignment: NewDriverShiftAssignmentInput,
    driverTimezone: string
  ) => {
    try {
      const driverShift =
        driverShiftAssignment.driverShiftId !== "NEW"
          ? (
              await useGetDriverShiftDetailsQuery.fetcher({
                id: driverShiftAssignment.driverShiftId,
              })()
            ).driverShiftById
          : null;
      if (driverShiftAssignment.driverShiftId === "NEW" || driverShift?.isAdhoc) {
        const zonedStartDate = utcToZonedTime(
          driverShiftAssignment.startDate,
          driverTimezone
        );
        const zonedEndDate = utcToZonedTime(
          driverShiftAssignment.endDate,
          driverTimezone
        );
        const { driverById: driver } = await useGetDriverDetailsQuery.fetcher({
          id: driverShiftAssignment.driverId,
        })();
        const newDriverShift: NewDriverShiftInput = {
          driverIds: [driverShiftAssignment.driverId],
          domicileId: driver.domicileId,
          startTime: {
            hour: zonedStartDate.getHours(),
            minute: zonedStartDate.getMinutes(),
          },
          endTime: {
            hour: zonedEndDate.getHours(),
            minute: zonedEndDate.getMinutes(),
          },
          days: eachDayOfInterval({
            start: zonedStartDate,
            end: zonedEndDate,
          }).map((date) => date.getDay()),
          label: `Custom Driver Shift`,
          isAdhoc: true,
        };
        const response =
          driverShiftAssignment.driverShiftId === "NEW"
            ? await addDriverShiftMutation.mutateAsync({
                newDriverShiftData: newDriverShift,
              })
            : await editDriverShiftMutation.mutateAsync({
                editDriverShiftData: newDriverShift,
                id: driverShiftAssignment.driverShiftId,
              });
        driverShiftAssignment.driverShiftId =
          "addDriverShift" in response
            ? response.addDriverShift._id
            : response.editDriverShift._id;
      }
      const newLinkingHasPeriod =
        !!driverShiftAssignment.startDate && !!driverShiftAssignment.endDate;
      const existingLinkingHasPeriod =
        !!existingDriverShiftAssignment?.startDate &&
        !!existingDriverShiftAssignment?.endDate;
      // If we are editing an existing link and set a period, we should create a new one
      // instead of overriding the existing one
      if (
        existingDriverShiftAssignment?._id &&
        (existingLinkingHasPeriod || !newLinkingHasPeriod)
      ) {
        const response = await editDriverShiftAssignmentMutation.mutateAsync({
          id: existingDriverShiftAssignment._id,
          editDriverShiftAssignmentData: driverShiftAssignment,
        });
        props.onSaved?.(response.editDriverShiftAssignment._id);
      } else {
        const response = await addDriverShiftAssignmentMutation.mutateAsync({
          newDriverShiftAssignmentData: driverShiftAssignment,
        });
        props.onSaved?.(response.addDriverShiftAssignment._id);
      }
      driverShiftAssignmentQuery.refetch();
      props.onClose();
    } catch (error) {
      // Set the error in local state so it can be displayed
      // Don't close the modal so the user can see the error
      console.error("Error submitting driver shift assignment:", error);
      setSubmissionError(error as Error);
    }
  };

  if (props.driverShiftAssignmentId && driverShiftAssignmentQuery.isLoading) {
    return <LoadingOverlay loading />;
  }

  return (
    <DriverShiftAssignmentFormModal
      driverShiftAssignment={driverShiftAssignmentToInput(
        existingDriverShiftAssignment
      )}
      onSubmit={submitDriverShiftAssignment}
      loading={
        addDriverShiftAssignmentMutation.isLoading ||
        editDriverShiftAssignmentMutation.isLoading ||
        addDriverShiftMutation.isLoading ||
        editDriverShiftMutation.isLoading ||
        (!!props.driverShiftAssignmentId &&
          driverShiftAssignmentQuery?.isLoading)
      }
      error={
        submissionError ||
        addDriverShiftAssignmentMutation.error ||
        editDriverShiftAssignmentMutation.error ||
        addDriverShiftMutation.error ||
        editDriverShiftMutation.error
      }
      {...props}
    />
  );
};

export default DriverShiftAssignmentFormModalContainer;
